// Rol de negocio del practicante logueado (2026-09-11) — Medplum solo expone
// especialidad clínica (useEspecialidadDelPracticante), nunca el tipo de
// cuenta ("admin"/"personal-medico"/"director-tecnico-farmacia"), que vive
// solo en hce_users (packages/web). Sin esto no hay forma de restringir el
// nav del Director Técnico Q.F./B.F. a solo Farmacia/Medicamentos. Mismo
// patrón que useFarmaciaInternaHabilitada.ts: fetch propio, sin caché de
// librería, useEffect con guarda de cancelado.
import { useEffect, useState } from 'react';
import { vaultUrl } from './hceVault';
import { cabecerasBoveda } from './audit/auditedFetch';

export type RolHce = 'admin' | 'personal-medico' | 'director-tecnico-farmacia' | null;
/**
 * `matricula` (2026-09-12): registro profesional de hce_users — el MISMO que
 * el servidor imprime en la firma (/api/hce/firmar-documento) y en la receta
 * (/api/hce/emitir-receta); el navegador ya no lo tipea, solo lo muestra.
 */
export type EstadoRolHce =
  | { cargando: true }
  | { cargando: false; rol: RolHce; matricula: string | null; farmaciaInternaActiva: boolean; acciones?: string[] | null };

/** Acciones que el servidor comprueba por PROFESIÓN (BA-C-11): lo que /api/hce/mi-rol devuelve en `acciones`. */
export type AccionProfesionalHce = 'prescribir' | 'firmar-documento-clinico' | 'documento-al-paciente' | 'alta-paciente' | 'firmar-adenda';

/**
 * ¿Muestro el botón de esta acción? Solo para la interfaz: el servidor la rechaza con 403 si la profesión no está habilitada. Mientras carga, o si no
 * hay dato de acciones (servidor viejo, error de red), se muestra (no se esconde nada por no saber); solo se oculta si el servidor dijo que NO.
 */
export function puedeAccionProfesional(estado: EstadoRolHce, accion: AccionProfesionalHce): boolean {
  if (estado.cargando || !Array.isArray(estado.acciones)) return true;
  return estado.acciones.includes(accion);
}

export function useRolDelPracticante(): EstadoRolHce {
  const [datos, setDatos] = useState<{ rol: RolHce; matricula: string | null; farmaciaInternaActiva: boolean; acciones: string[] | null }>();

  useEffect(() => {
    let cancelado = false;
    fetch(vaultUrl('/api/hce/mi-rol'), { headers: cabecerasBoveda() })
      .then((res) => (res.ok ? res.json() : { role: null, matricula: null, farmaciaInternaActiva: false, acciones: null }))
      .then((body) => {
        if (cancelado) return;
        const matricula = typeof body?.matricula === 'string' && body.matricula.trim() ? body.matricula.trim() : null;
        setDatos({ rol: body?.role ?? null, matricula, farmaciaInternaActiva: body?.farmaciaInternaActiva === true, acciones: Array.isArray(body?.acciones) ? body.acciones : null });
      })
      .catch(() => {
        if (!cancelado) setDatos({ rol: null, matricula: null, farmaciaInternaActiva: false, acciones: null });
      });
    return () => {
      cancelado = true;
    };
  }, []);

  if (datos === undefined) return { cargando: true };
  return { cargando: false, ...datos };
}

export function esDirectorTecnicoFarmacia(estado: EstadoRolHce): boolean {
  return !estado.cargando && estado.rol === 'director-tecnico-farmacia';
}

/**
 * Versión no-hook, para el destino post-login/registro (LandingAcceso.tsx)
 * — ahí hace falta el rol dentro de un manejador de evento, no del render de
 * un componente, justo después de `medplum.processCode`.
 */
export async function rolDelPracticanteActual(): Promise<RolHce> {
  try {
    const res = await fetch(vaultUrl('/api/hce/mi-rol'), { headers: cabecerasBoveda() });
    if (!res.ok) return null;
    const body = await res.json();
    return body?.role ?? null;
  } catch {
    return null;
  }
}
